import { Pagination, paginationToParams } from '../Pagination';
import { SortingOrder } from '../SortingOrder';
import { QueryParameter } from '../QueryParameter';

export type ProductOrderField =
  | 'id'
  | 'quantity'
  | 'price'
  | 'name'
  | 'status'
  | 'created-at';

export interface GetProductsRequest extends Pagination {
  search: string;
  includeInactives: boolean;
  orderBy: ProductOrderField;
  order: SortingOrder;
}

export const getDefaultProductFilters = (): GetProductsRequest => ({
  search: '',
  includeInactives: true,
  orderBy: 'name',
  order: 'asc',
  page: 0,
  pageSize: 10,
});

export const getProductsRequestToParams = (request: GetProductsRequest) => {
  let order = '';
  switch (request.orderBy) {
    case 'id': {
      order = request.order === 'asc' ? 'orderByIDAsc' : 'orderByIDDesc';
      break;
    }
    case 'quantity': {
      order =
        request.order === 'asc' ? 'orderByQuantityAsc' : 'orderByQuantityDesc';
      break;
    }
    case 'price': {
      order = request.order === 'asc' ? 'orderByPriceAsc' : 'orderByPriceDesc';
      break;
    }
    case 'name': {
      order = request.order === 'asc' ? 'orderByNameAsc' : 'orderByNameDesc';
      break;
    }
    case 'status': {
      order =
        request.order === 'asc' ? 'orderByStatusAsc' : 'orderByStatusDesc';
      break;
    }
    default: {
      order =
        request.order === 'asc'
          ? 'orderByCreatedAtAsc'
          : 'orderByCreatedAtDesc';
      break;
    }
  }

  return {
    search: request.search,
    'include-inactives': request.includeInactives,
    order,
    ...paginationToParams(request),
  };
};

export const getProductsRequestToQueryParams = (
  request: GetProductsRequest,
) => {
  const defaultFilters = getDefaultProductFilters();
  const params: QueryParameter[] = [];

  if (request.search) {
    params.push({ key: 'search', value: request.search });
  }

  if (!request.includeInactives) {
    params.push({ key: 'include-inactives', value: 'false' });
  }

  if (request.orderBy !== defaultFilters.orderBy) {
    params.push({ key: 'order-by', value: request.orderBy });
  }

  if (request.order !== defaultFilters.order) {
    params.push({ key: 'order', value: request.order });
  }

  if (request.page !== defaultFilters.page) {
    params.push({ key: 'page', value: request.page.toString() });
  }

  if (request.pageSize !== defaultFilters.pageSize) {
    params.push({ key: 'page-size', value: request.pageSize.toString() });
  }

  return params;
};

export const getProductsRequestFromQueryParams = (
  params: URLSearchParams,
): GetProductsRequest => {
  const request = getDefaultProductFilters();

  const search = params.get('search');
  if (search) {
    request.search = search;
  }

  const includeInactives = params.get('include-inactives');
  if (includeInactives) {
    request.includeInactives = includeInactives === 'true';
  }

  const orderBy = params.get('order-by');
  if (orderBy) {
    request.orderBy = orderBy as ProductOrderField;
  }

  const order = params.get('order');
  if (order) {
    request.order = order as SortingOrder;
  }

  const page = params.get('page');
  if (page) {
    request.page = Number(page);
  }

  const pageSize = params.get('page-size');
  if (pageSize) {
    request.pageSize = Number(pageSize);
  }

  return request;
};
